import { Component, computed, Injector, input, OnDestroy, OnInit, output } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { takeWhile } from 'rxjs';
import { AnimateOpenDirective } from '../../../../../../../shared/directives/animate-open.directive';
import { templateDrivenFormsViewProviders } from '../../../../../../../shared/directives/form.directive';
import { ServiceOptions } from '../../../../../constants/service-options.enum';
import { OrbitFormClaimService } from '../../../../services/orbit-form-claim.service';
import { getErrorsFromSubmission } from '../../pipes/error-subscription';
import { formValidationGenerator, FormValidators } from '../../pipes/form-validation';

@Component({
  selector: 'app-service-options',
  imports: [FormsModule, AnimateOpenDirective],
  templateUrl: './service-options.component.html',
  styleUrl: './service-options.component.scss',
  viewProviders: [templateDrivenFormsViewProviders],
  host: {
    class: 'flex-col gap-16',
  },
})
export class ServiceOptionsComponent implements OnInit, OnDestroy {
  formValue = input<Record<string, any>>({});
  formSubmitted = input(false);
  selectedOption: any;
  formValid = output<boolean>();

  gameReplacementQA = [
    {
      question: 'When will I get my replacement?',
      answer: 'Usually around 3 working days after we collect your broken item.',
      open: false,
    },
    {
      question: 'Will it be a brand new replacement?',
      answer: 'Your replacement will be brand new or Grade A refurbished.',
      open: false,
    },
    {
      question: 'Will it be the same colour?',
      answer:
        'Because your plan doesn’t cover cosmetic issues, we can’t guarantee the replacement will be the same colour.',
      open: false,
    },
    {
      question: 'Why is my plan being cancelled?',
      answer:
        'If you choose to have your product replaced (instead of repaired), your plan will end in line with the terms and conditions.',
      open: false,
    },
    {
      question: 'Can I protect my new console?',
      answer: 'Sorry, we don’t offer protection for replacement consoles at the moment, but we might in the future.',
      open: false,
    },
  ];

  private _destroyed = false;

  private viewModel = computed(() => {
    const serviceOptions = this.orbitFormClaimService.state.latestStep.serviceOptions;

    return {
      formValue: this.formValue(),
      serviceOptions: serviceOptions.map(this.addDisplayInformation),
      submittingResource: this.orbitFormClaimService.formSubmission,
      isGameReplacement: this.selectedOption?.serviceOptionLabel?.toLowerCase()?.includes('game exchange'),
      isEligibleForReplacement: !!serviceOptions.find((i: any) =>
        i.serviceOptionLabel?.toLowerCase()?.includes('game exchange')
      ),
    };
  });

  get vm() {
    return this.viewModel();
  }

  constructor(
    private readonly injector: Injector,
    private readonly orbitFormClaimService: OrbitFormClaimService
  ) {}

  ngOnDestroy(): void {
    this._destroyed = true;
  }

  ngOnInit() {
    this.handleFormValidation();
    this.handleAPIErrors();
  }

  handleAPIErrors(): void {
    getErrorsFromSubmission(this.vm.submittingResource, this.injector, (attempts, error) => {
      console.log({ error });
    });
  }

  handleFormValidation(): void {
    toObservable(this.formValue, { injector: this.injector })
      .pipe(
        formValidationGenerator(
          {
            option: [FormValidators.required],
          },
          []
        ),
        takeWhile(() => this._destroyed)
      )
      .subscribe({
        next: ({ isFormValid }) => {
          this.formValid.emit(isFormValid);
        },
      });
  }

  addDisplayInformation(option: any) {
    let display = {};

    switch (option.serviceOptionType) {
      case ServiceOptions.HomeRepair:
        display = {
          icon: 'fa-light fa-wrench',
          label: 'Engineer visit',
          description: ['Pick a day for an expert engineer to visit and repair your appliance'],
        };
        break;
      case ServiceOptions.Courier:
        if ((option.serviceOptionLabel ?? '').toLowerCase().includes('game exchange')) {
          display = {
            image: 'replace',
            label: 'Collect and replace',
            description: [
              'Book a date for your broken console to be collected. Once we’ve checked it, we’ll deliver a replacement back to you (usually in 3 working days).',
            ],
          };
        } else {
          display = {
            image: 'courier',
            label: 'Courier collection',
            description: [
              'We’ll collect your faulty product, repair it, and return it to you.',
              'If it can’t be repaired, we’ll send you a replacement.',
            ],
          };
        }

        break;
      case ServiceOptions.DropOff:
        display = {
          image: 'drop_off',
          label: 'Drop-off for collection',
          description: [option.serviceOptionMessage],
        };
        break;
      case ServiceOptions.ManualReferral:
        display = {
          icon: 'fa-light fa-headset',
          label: 'Contact the service provider',
          description: ['Get in touch with the service provider directly and arrange for your item to be fixed.'],
        };
        break;
      case ServiceOptions.SelfSend:
        display = {
          image: 'self_send',
          label: 'Arrange a self-send',
          description: [
            'Send us your faulty product so we can repair it. If we can’t repair it, we’ll send you a replacement. DPD will email your drop‑off instructions and update you on progress.',
          ],
        };
        break;
      case ServiceOptions.PayAndClaim:
        display = {
          image: 'pay_and_claim',
          label: 'Pay and claim',
          description: [
            'Contact a local repairer for a quote. Once they’ve fixed your item, let us know the cost and we’ll reimburse you.',
          ],
        };
        break;
      default:
        display = {};
        break;
    }
    return { ...option, display };
  }
}
